import {AGENCY_KEYS} from "@/api/agencyApi";
import {stripParamKeys} from "@/api/influencerApi";
import {USER_KEYS} from "@/api/userApi";
import type {
  AdminAmbassadorSerializer,
  AdminAmbassadorStatsSerializer,
  AdminAmbassadorStatsViewSerializer,
  AmbassadorshipEligibilitySerializer,
  AmbassadorshipLimitsSerializer,
  AmbassadorshipRequestBrandSerializer,
  CampaignDashboardSerializer,
  InfluencerBlockSerializer,
  PotentialAmbassadorSerializer,
  PublicBrandSerializer,
  SubBrandInviteSerializer,
} from "@/generated/DjangoTypes";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsOwnProfile} from "@/hooks/useUserHooks";
import {extractCutoffFromPageParams, sumCountsByCutoffGroup} from "@/hooks/useVibeSearchBroadening";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useComposedCampaignState} from "@/store/campaignForm";
import useCampaignManagementStore from "@/store/campaignManagement";
import type {TFilters} from "@/store/filterStoreUtils";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import type {
  TAccountMember,
  TAgency,
  TAmbassadorBulkDecision,
  TCompanyProfile,
  TInfiniteQueryOptions,
  TPaginated,
  TPermission,
  TQueryOptions,
  TQueryResultWithCount,
  TReferralForm,
  TSingleBrandCustomer,
  TVibePaginated,
  TVibeQueryResult,
} from "@/store/types";
import useUserStore from "@/store/user";
import {useMutation, type UseInfiniteQueryResult} from "@tanstack/react-query";
import {useLocation, useParams} from "@tanstack/react-router";
import type {AxiosError, AxiosResponse} from "axios";
import {produce} from "immer";
import {useAtomValue} from "jotai";
import qs from "qs";
import {toast} from "react-toastify";

export const COMPANY_KEYS = {
  mCompany: (companyId: number) => [`/company/${companyId}/`],
  mCompanyAdditionalLimitRequest: (subBrandId: number) => [`/sub_brand/${subBrandId}/request_additional_limit/`],
  mCompanyAmbassadorshipRequestAcceptance: (ambassadorshipRequestId: number) => [
    `/ambassadorship_requests/${ambassadorshipRequestId}/accept/`,
  ],
  mCompanyAmbassadorshipRequestRejection: (ambassadorshipRequestId: number) => [
    `/ambassadorship_requests/${ambassadorshipRequestId}/decline/`,
  ],
  mCompanyAmbassadorshipRequestSend: ["/ambassadorship_requests/"],
  mCompanyBrandAmbassadorAcceptance: (companyId: number) => [`/company/${companyId}/accept_brand_ambassador/`],
  mCompanyBrandAmbassadorRejection: (companyId: number) => [`/company/${companyId}/reject_brand_ambassador/`],
  mCompanyBrandAmbassadorsBulkReview: (companyId: number, decision: TAmbassadorBulkDecision) => [
    `/company/${companyId}/${decision}_all_brand_ambassadors/`,
  ],
  mCompanyDraftCampaignsDelete: (companyId: number) => [`/company/${companyId}/delete_draft_campaigns/`],
  mCompanyFavouriteInfluencer: (companyId: number) => [`/company/${companyId}/favourite_influencer/`],
  mCompanyInfluencerBlock: (companyId: number) => [`/company/${companyId}/block_influencer/`],
  mCompanyInfluencerUnblock: (companyId: number) => [`/company/${companyId}/unblock_influencer/`],
  mCompanyMemberInvite: (companyId: number) => [`/sub_brand/${companyId}/invite_user/`],
  mCompanyMemberInviteRemoval: (companyId: number) => [`/sub_brand/${companyId}/remove_invite/`],
  mCompanyMemberPermissionUpdate: (companyId: number) => [`/sub_brand/${companyId}/update_permission/`],
  mCompanyMemberPermissionUpdateRequest: (companyId: number) => [`/sub_brand/${companyId}/request_permission_upgrade/`],
  mCompanyMemberPermissionUpdateResponse: (companyId: number) => [
    `/sub_brand/${companyId}/respond_permission_upgrade/`,
  ],
  mCompanyMemberRegistration: (companyId: number) => [`/sub_brand/${companyId}/register_sub_brand_user/`],
  mCompanyMemberRemoval: (companyId: number) => [`/sub_brand/${companyId}/remove_user/`],
  mCompanyRanking: (companyId: number) => [`/company/${companyId}/update_ranking/`],
  mCompanyJoinApply: (slug: string) => [`/company/join/${slug}/apply/`],
  qCompany: (companyId: number) => [`/company/${companyId}/`],
  qCompanyAllBrandAmbassadors: (params = "") => [`/company/all_brand_ambassadors/${params ? `?${params}` : ""}`],
  qCompanyAmbassadorPending: (companyId: number, influencerId: number) => [
    `/company/${companyId}/potential_brand_ambassadors/?influencer_id=${influencerId}`,
  ],
  qCompanyAmbassadors: (companyId: number) => [`/company/${companyId}/ambassadors/`],
  qCompanyAmbassadorsPending: (companyId: number, params = "") => [
    `/company/${companyId}/potential_brand_ambassadors/${params ? `?${params}` : ""}`,
  ],
  qCompanyAmbassadorsPendingCount: (companyId: number, params = "") => [
    `/company/${companyId}/potential_brand_ambassadors/?count_only=true${params ? `&${params}` : ""}`,
  ],
  qCompanyAmbassadorsPendingNetworkData: (companyId: number, params = "") => [
    `/company/${companyId}/potential_brand_ambassadors_network_data/${params ? `?${params}` : ""}`,
  ],
  qCompanyAmbassadorshipEligibility: (companyId: number, influencerId: number) => [
    `/ambassadorship_requests/eligibility/?company_profile_id=${companyId}&influencer_id=${influencerId}`,
  ],
  qCompanyAmbassadorshipRequests: (companyId: number, params = "") => [
    `/ambassadorship_requests/?company_profile_id=${companyId}&kind=request${params ? `&${params}` : ""}`,
  ],
  qCompanyBlockedInfluencers: (companyId: number) => [`/company/${companyId}/blocked_influencers/`],
  qCompanyBrandAmbassadorStats: () => ["/company/brand_ambassador_stats/"],
  qCompanyCampaigns: (params?: {
    companyId?: number;
    status: "active" | "complete" | "drafts" | "paused" | "planned" | "open";
  }) => [`/company/${params?.companyId}/${params?.status ?? "all"}/`],
  qCompanyChatCampaigns: (companyId: number) => [`/company/${companyId}/chat_campaigns/`],
  qCompanyCollaborations: (companyId: number, params?: string) => [`/company/${companyId}/collaborations/?${params}`],
  qCompanyDetails: (companyId: number) => [`/company/${companyId}/details/`],
  qCompanyFollowers: (companyId: number, params?: string) => [`/company/${companyId}/followers/?${params}`],
  qCompanyInvites: (companyId: number) => [`/sub_brand/${companyId}/invites/`],
  qCompanyList: (params?: string) => [`/sub_brand/?subscriptions=true&${params}`],
  qCompanyMembers: (companyId: number) => [`/sub_brand/${companyId}/members/`],
  qCompanyNotificationCounts: (companyId: number) => [`/company/${companyId}/message_counts/`],
  qCompanyOpenCampaigns: (companyId: number) => [`/company/${companyId}/open_campaigns/`],
  qCompanyJoin: (slug: string) => [`/company/join/${slug}/`],
  qCompanyPublicBrands: (filters?: {company_name?: string; country?: string}) => [
    `/company/public_brands/?${qs.stringify(filters ?? {})}`,
  ],
  qCompanyPublicRatings: (slug: string) => [`company/rating_stats/${slug}`],
  qCompanyRatings: (companyId: number, params?: string) => [`/company/${companyId}/ratings/?${params}`],
  qCompanyRecommendedInfluencers: (companyId: number) => [`/company/${companyId}/recommended_influencers/`],
  qCompanyReferralStatistics: (companyId: number) => [`/company/${companyId}/public_referral_stats/`],
} as const;

export const matchCompanyAmbassadorsPending = (companyId: number) => ({
  predicate: (query: {queryKey: unknown}) =>
    Array.isArray(query.queryKey) &&
    typeof query.queryKey[0] === "string" &&
    query.queryKey[0].startsWith(`/company/${companyId}/potential_brand_ambassadors/`),
});

export const matchAmbassadorshipAffectedInfluencers = {
  predicate: (query: {queryKey: unknown}) =>
    Array.isArray(query.queryKey) &&
    typeof query.queryKey[0] === "string" &&
    ["/group/", "/influencer/"].some(prefix => (query.queryKey as string[])[0].startsWith(prefix)),
};

const refreshBlocklistAffectedQueries = (companyId: number) =>
  Promise.all(
    [
      {queryKey: COMPANY_KEYS.qCompanyBlockedInfluencers(companyId)},
      {
        predicate: (query: {queryKey: unknown}) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === "string" &&
          ["/campaign/", "/group/", "/influencer/?"].some(prefix => (query.queryKey as string[])[0].startsWith(prefix)),
      },
    ].map(filter => {
      queryClient.removeQueries({...filter, type: "inactive"});
      return queryClient.invalidateQueries(filter);
    }),
  );

export const useBlockInfluencerMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?(): void;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("groups:influencer_blocked")},
    mutationKey: COMPANY_KEYS.mCompanyInfluencerBlock(companyId),
    mutationFn: ({campaignApplicationId, influencerId}: {campaignApplicationId?: number; influencerId: number}) =>
      client.post<InfluencerBlockSerializer>(COMPANY_KEYS.mCompanyInfluencerBlock(companyId)[0], {
        campaign_application_id: campaignApplicationId,
        influencer_id: influencerId,
      }),
    onSuccess: async () => {
      await refreshBlocklistAffectedQueries(companyId);
      onSuccessCallback?.();
    },
  });

export const useCompanyBlockedInfluencersQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TInfiniteQueryOptions<
    InfluencerBlockSerializer,
    AxiosError,
    TQueryResultWithCount<InfluencerBlockSerializer[]>
  >;
}) =>
  useGenericInfiniteQueryWithCount<InfluencerBlockSerializer>({
    queryKey: COMPANY_KEYS.qCompanyBlockedInfluencers(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useUnblockInfluencerMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?(): void;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("groups:influencer_unblocked")},
    mutationKey: COMPANY_KEYS.mCompanyInfluencerUnblock(companyId),
    mutationFn: ({influencerId}: {influencerId: number}) =>
      client.post(COMPANY_KEYS.mCompanyInfluencerUnblock(companyId)[0], {influencer_id: influencerId}),
    onSuccess: async () => {
      await refreshBlocklistAffectedQueries(companyId);
      onSuccessCallback?.();
    },
  });

export const useAdminAmbassadorStatsQuery = () =>
  useGenericQuery<AdminAmbassadorStatsViewSerializer>({
    queryKey: COMPANY_KEYS.qCompanyBrandAmbassadorStats(),
  });

export const useAdminAmbassadorsQuery = (params = "") =>
  useGenericInfiniteQuery<
    AdminAmbassadorSerializer,
    AxiosError,
    TQueryResultWithCount<AdminAmbassadorSerializer[]> & {stats?: AdminAmbassadorStatsSerializer}
  >({
    queryKey: COMPANY_KEYS.qCompanyAllBrandAmbassadors(params),
    select: response => ({
      count: response.pages[0].data.count,
      results: response.pages.flatMap(page => page.data.results) as unknown as AdminAmbassadorSerializer[],
      stats: (response.pages[0].data as unknown as {stats?: AdminAmbassadorStatsSerializer}).stats,
    }),
  });

export const useAmbassadorshipEligibilityQuery = ({
  companyId,
  influencerId,
  queryOptions,
}: {
  companyId: number;
  influencerId: number;
  queryOptions?: TQueryOptions<AmbassadorshipEligibilitySerializer>;
}) =>
  useGenericQuery<AmbassadorshipEligibilitySerializer>({
    queryKey: COMPANY_KEYS.qCompanyAmbassadorshipEligibility(companyId, influencerId),
    enabled: !!companyId && !!influencerId,
    ...queryOptions,
  });

export const useCompanyAmbassadorshipRequestsQuery = ({
  companyId,
  queryOptions,
  status,
}: {
  companyId: number;
  queryOptions?: TInfiniteQueryOptions<
    AmbassadorshipRequestBrandSerializer,
    AxiosError,
    TQueryResultWithCount<AmbassadorshipRequestBrandSerializer[]> & {limits: AmbassadorshipLimitsSerializer | null}
  >;
  status?: AmbassadorshipRequestBrandSerializer["status"] | null;
}) =>
  useGenericInfiniteQuery<
    AmbassadorshipRequestBrandSerializer,
    AxiosError,
    TQueryResultWithCount<AmbassadorshipRequestBrandSerializer[]> & {limits: AmbassadorshipLimitsSerializer | null}
  >({
    queryKey: COMPANY_KEYS.qCompanyAmbassadorshipRequests(companyId, status ? `status=${status}` : ""),
    enabled: !!companyId,
    select: response => ({
      count: response.pages[0].data.count,
      limits: (
        response.pages[0].data as TPaginated<AmbassadorshipRequestBrandSerializer> & {
          limits: AmbassadorshipLimitsSerializer | null;
        }
      ).limits,
      results: response.pages.flatMap(page => page.data.results),
    }),
    ...queryOptions,
  });

export const useInviteCompanyMemberMutation = ({
  companyId,
  email,
  onSuccessCallback,
}: {
  companyId: number;
  email: string;
  onSuccessCallback?: (permission: TPermission) => void;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompanyMemberInvite(companyId),
    // The permission is a mutate() argument so a value chosen in the same event is sent, not the last rendered one
    mutationFn: (permission: TPermission) =>
      client.post(COMPANY_KEYS.mCompanyMemberInvite(companyId)[0], {
        emails_data: email,
        permission,
      }),
    onSuccess: (_data, permission) => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyInvites(companyId)});
      onSuccessCallback?.(permission);
    },
  });

export const useUpdateCompanyMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?(): void;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompany(companyId),
    mutationFn: (data: Partial<TCompanyProfile>) => client.patch(COMPANY_KEYS.mCompany(companyId)[0], data),
    onSuccess: () => {
      onSuccessCallback?.();
    },
  });

export const useUpdateCompanyMemberPermissionMutation = ({
  companyMemberUserId,
  companyId,
}: {
  companyMemberUserId: number;
  companyId: number;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("multi_account:permission_update_success")},
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdate(companyId),
    mutationFn: ({newPermission}: {newPermission: TPermission}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdate(companyId)[0], {
        user_id: companyMemberUserId,
        permission: newPermission,
        action: "set",
      }),
    onSuccess: () => {
      const userId = Number(useUserStore.getState().user.id);
      [COMPANY_KEYS.qCompanyMembers(companyId), USER_KEYS.qUser(userId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });

export const useRequestPermissionUpgradeMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?: () => void;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("multi_account:permission_request_success")},
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdateRequest(companyId),
    mutationFn: ({newPermission}: {newPermission: TPermission}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdateRequest(companyId)[0], {permission: newPermission}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});
      onSuccessCallback?.();
    },
  });

export const useRespondToPermissionUpgradeRequestMutation = ({companyId}: {companyId: number}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdateResponse(companyId),
    mutationFn: ({user_id, status}: {user_id: number; status: "ACCEPTED" | "REJECTED"}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdateResponse(companyId)[0], {user_id, status}),
    onSuccess: response => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});
      toast.success(i18n.t(`multi_account:${response.data.detail}`));
    },
  });

export const useRequestReferralCodeMutation = (companySlug: string) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: COMPANY_KEYS.mCompanyJoinApply(companySlug),
    mutationFn: (form: TReferralForm) =>
      client
        .post<{
          referral_code: string;
        }>(COMPANY_KEYS.mCompanyJoinApply(companySlug)[0], {
          email: form.email,
          handle: form.handle,
          platform: form.platform,
        })
        .then(response => response.data),
    onSuccess: () =>
      toast.success(i18n.t("referral:referral_sent"), {
        className:
          "border-success-medium-green bg-success-light-green text-success-medium-green m-2 sm:w-fit w-[90vw] rounded-lg border p-3.5 max-md:mb-20",
      }),
  });

export const useCompanyQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TQueryOptions<TCompanyProfile>;
}) =>
  useGenericQuery<TCompanyProfile>({
    queryKey: COMPANY_KEYS.qCompany(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyCampaignsQuery = ({
  agencyId,
  companyId,
  status,
  queryOptions,
}: {
  agencyId?: number;
  companyId?: number;
  status: "active" | "complete" | "drafts" | "paused" | "planned" | "open";
  queryOptions: TInfiniteQueryOptions<
    CampaignDashboardSerializer,
    AxiosError,
    TQueryResultWithCount<CampaignDashboardSerializer[]>
  >;
}) =>
  useGenericInfiniteQueryWithCount<CampaignDashboardSerializer>({
    queryKey:
      !!agencyId && !companyId
        ? AGENCY_KEYS.qAgencyCampaigns({agencyId, status})
        : COMPANY_KEYS.qCompanyCampaigns({companyId, status}),
    ...queryOptions,
  });

export const useCompanyInvitesQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TQueryOptions<SubBrandInviteSerializer[]>;
}) =>
  useGenericQuery<SubBrandInviteSerializer[]>({
    queryKey: COMPANY_KEYS.qCompanyInvites(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyListQuery = ({
  params,
  queryOptions,
}: {
  params?: string;
  queryOptions?: TInfiniteQueryOptions<TSingleBrandCustomer, AxiosError, TQueryResultWithCount<TSingleBrandCustomer[]>>;
} = {}) =>
  useGenericInfiniteQueryWithCount<TSingleBrandCustomer>({
    queryKey: COMPANY_KEYS.qCompanyList(params),
    ...queryOptions,
  });

export const usePublicBrandsQuery = ({
  company_name,
  country,
  queryOptions,
}: {
  company_name?: string;
  country?: string;
  queryOptions?: TInfiniteQueryOptions<
    PublicBrandSerializer,
    AxiosError,
    TQueryResultWithCount<PublicBrandSerializer[]>
  >;
} = {}) =>
  useGenericInfiniteQueryWithCount<PublicBrandSerializer>({
    queryKey: COMPANY_KEYS.qCompanyPublicBrands({company_name, country}),
    ...queryOptions,
  });

export const useCompanyMembersQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TQueryOptions<TAccountMember[]>;
}) =>
  useGenericQuery<TAccountMember[]>({
    queryKey: COMPANY_KEYS.qCompanyMembers(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyPendingAmbassadorQuery = ({
  companyId,
  influencerId,
  queryOptions,
}: {
  companyId: number;
  influencerId: number;
  queryOptions?: TQueryOptions<TPaginated<PotentialAmbassadorSerializer>>;
}) =>
  useGenericQuery<TPaginated<PotentialAmbassadorSerializer>>({
    queryKey: COMPANY_KEYS.qCompanyAmbassadorPending(companyId, influencerId),
    enabled: !!companyId && !!influencerId,
    ...queryOptions,
  });

export const useCompanyPendingAmbassadorsQuery = ({
  companyId,
  queryOptions,
  searchParams,
}: {
  companyId: number;
  queryOptions?: TInfiniteQueryOptions<
    PotentialAmbassadorSerializer,
    AxiosError,
    TVibeQueryResult<PotentialAmbassadorSerializer>
  >;
  searchParams?: string;
}) => {
  const stableParams = searchParams ? stripParamKeys(searchParams) : searchParams;

  return useGenericInfiniteQueryWithCount({
    queryKey: COMPANY_KEYS.qCompanyAmbassadorsPending(companyId, stableParams),
    initialPageParam: stableParams ? COMPANY_KEYS.qCompanyAmbassadorsPending(companyId, stableParams)[0] : undefined,
    enabled: !!companyId,
    select: response => {
      const firstPage = response.pages[0]?.data as TVibePaginated<PotentialAmbassadorSerializer>;

      return {
        count: sumCountsByCutoffGroup(response.pageParams, response.pages),
        cutoffValue: extractCutoffFromPageParams(response.pageParams),
        frequencyDistribution: firstPage?.frequency_distribution ?? {},
        noCutoff: !!firstPage?.no_cutoff,
        results: response.pages.flatMap(page => page.data.results),
      };
    },
    ...queryOptions,
  }) as UseInfiniteQueryResult<TVibeQueryResult<PotentialAmbassadorSerializer>>;
};

export const useReviewAllPendingAmbassadorsMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?: () => void;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationFn: ({decision, filters}: {decision: TAmbassadorBulkDecision; filters: Partial<TFilters>}) =>
      client
        .post<{count: number}>(COMPANY_KEYS.mCompanyBrandAmbassadorsBulkReview(companyId, decision)[0], {filters})
        .then(response => response.data),
    onSuccess: ({count}, {decision}) => {
      capturePostHogEvent(
        decision === "accept"
          ? PostHogEvents.PendingAmbassadorsBulkAccepted
          : PostHogEvents.PendingAmbassadorsBulkDeclined,
        {companyId, count},
      );
      [
        {queryKey: COMPANY_KEYS.qCompanyAmbassadors(companyId)},
        matchCompanyAmbassadorsPending(companyId),
        {queryKey: COMPANY_KEYS.qCompanyReferralStatistics(companyId)},
      ].map(filter => queryClient.invalidateQueries({...filter, refetchType: "all"}));
      if (decision === "accept") {
        queryClient.invalidateQueries({
          predicate: query => typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith("/group/"),
        });
      }
      toast(
        i18n.t(
          decision === "accept" ? "referral:accept_all_ambassadors_queued" : "referral:decline_all_ambassadors_queued",
          {
            count,
          },
        ),
        {type: "success"},
      );
      onSuccessCallback?.();
    },
  });

export const useCurrentCompanyId = () => {
  const {companyId: companyIdFromUrl} = useParams({strict: false});
  const {pathname} = useLocation();
  const managedCompanyId = useCampaignManagementStore(state => state.campaign?.company_profile?.id);
  const campaignFormCompanyId = useComposedCampaignState(state => state.misc.companyId);
  const selectedCompanyId = useAtomValue(selectedCompanyIdAtom);

  const managedCompanyIdIfRelevant = pathname.includes("/campaign/") ? managedCompanyId : undefined;

  return companyIdFromUrl ?? managedCompanyIdIfRelevant ?? campaignFormCompanyId ?? selectedCompanyId;
};

// TODO: use this hook instead of the useSelectedCompany hooks
export const useCurrentCompany = ({ownProfile, isEnabled = true}: {ownProfile?: boolean; isEnabled?: boolean} = {}) => {
  const companyId = useCurrentCompanyId();
  const isProfileOwnedByUser = useIsOwnProfile();
  const isOwnProfile = ownProfile || isProfileOwnedByUser;
  const loggedInUser = useUserStore(state => state.user);

  return useCompanyQuery({
    companyId: companyId!,
    queryOptions: {
      enabled: !!companyId && isEnabled && (isOwnProfile || loggedInUser?.user_type === "admin"),
    },
  });
};

export const useUpdateCreditsCache = (scope?: {agencyId?: number; companyId?: number}) => {
  const {data: company} = useCurrentCompany();
  const {agencyId: urlAgencyId} = useParams({strict: false});

  return (creditsDelta: number) => {
    const agencyId = scope ? scope.agencyId : (urlAgencyId ?? company?.agency.id);
    const companyId = scope ? scope.companyId : company?.id;

    if (agencyId) {
      queryClient.setQueryData<AxiosResponse<TAgency>>(AGENCY_KEYS.qAgency(agencyId), prevState =>
        prevState
          ? produce(prevState, draft => {
              draft.data.credits = +((draft.data.credits ?? 0) + creditsDelta).toFixed(2);
            })
          : prevState,
      );
    }

    if (companyId) {
      queryClient.setQueryData<AxiosResponse<TCompanyProfile>>(COMPANY_KEYS.qCompany(companyId), prevState =>
        prevState
          ? produce(prevState, draft => {
              draft.data.agency.credits = +((draft.data.agency.credits ?? 0) + creditsDelta).toFixed(2);
            })
          : prevState,
      );
    }
  };
};
